import React from 'react'
import { Input, Select } from '@/components/ui'
import type { OrganizationFormValues } from '@/features/organization/schemas/organizationForm.schema'
import type { FieldErrors, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import { FieldGrid, FieldGroup, SettingsPane } from '../organization-ui'
import { tenantHostLabel } from '@/lib/tenantHost'

const ORGANIZATION_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'deactivated', label: 'Deactivated' },
]

export interface IdentityPaneProps {
  register: UseFormRegister<OrganizationFormValues>
  watch: UseFormWatch<OrganizationFormValues>
  setValue: UseFormSetValue<OrganizationFormValues>
  errors: FieldErrors<OrganizationFormValues>
  disabled?: boolean
}

export const IdentityPane: React.FC<IdentityPaneProps> = ({
  register,
  watch,
  setValue,
  errors,
  disabled = false,
}) => {
  // The slug is the workspace address ({slug}.kaamco.com). Renaming it breaks
  // every link to the old one, so it is read-only here; KAAMCO support changes it.
  const slug = watch('slug') ?? ''
  const workspaceAddress = tenantHostLabel(slug) ?? slug

  return (
    <SettingsPane
      title="Workspace identity"
      description="How this organization is named, reached, and legally registered."
      action={
        <div className="w-full sm:w-52">
          <Select
            label="Status"
            options={ORGANIZATION_STATUS_OPTIONS}
            value={watch('status')}
            disabled={disabled}
            onChange={(value) =>
              setValue('status', value as OrganizationFormValues['status'], {
                shouldDirty: true,
              })
            }
          />
        </div>
      }
    >
      <FieldGroup label="Public profile">
        <FieldGrid>
          <Input
            label="Organization name"
            required
            error={errors.name?.message}
            disabled={disabled}
            {...register('name')}
          />
          <Input
            label="Workspace address"
            value={workspaceAddress}
            readOnly
            disabled
            hint="Contact KAAMCO support to change your workspace address."
          />
          <Input
            label="Website"
            type="url"
            placeholder="https://example.com"
            error={errors.website?.message}
            disabled={disabled}
            {...register('website')}
          />
        </FieldGrid>
      </FieldGroup>

      <FieldGroup label="Contact" hint="Used on staff-facing correspondence.">
        <FieldGrid>
          <Input
            label="Contact email"
            type="email"
            placeholder="admin@example.com"
            error={errors.contactEmail?.message}
            disabled={disabled}
            {...register('contactEmail')}
          />
          <Input
            label="Contact phone"
            type="tel"
            error={errors.contactPhone?.message}
            disabled={disabled}
            {...register('contactPhone')}
          />
        </FieldGrid>
      </FieldGroup>

      <FieldGroup label="Compliance" hint="Keep aligned with your official registration records.">
        <FieldGrid>
          <Input
            label="Registration number"
            error={errors.registrationNumber?.message}
            disabled={disabled}
            {...register('registrationNumber')}
          />
          <Input
            label="Tax ID"
            error={errors.taxId?.message}
            disabled={disabled}
            {...register('taxId')}
          />
          <Input
            label="Industry"
            error={errors.industry?.message}
            disabled={disabled}
            {...register('industry')}
          />
        </FieldGrid>
      </FieldGroup>
    </SettingsPane>
  )
}

IdentityPane.displayName = 'IdentityPane'
